import React, { useState, useEffect } from "react";

import Box from "@mui/material/Box";
import { alpha, styled } from "@mui/material/styles";
import {
  gridClasses,
  DataGrid,
  GridToolbar,
  GridColDef,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import { Alert, Button, Stack } from "@mui/material";
import { FiRefreshCcw, FiEdit } from "react-icons/fi";
import { Navigate } from "react-router-dom";
import { v4 as uuid } from "uuid";
import { AddBank } from "../components/AddBank";
import { AddBranch } from "../components/AddBranch";
import { QueryError, QueryLoading } from "./QueryStatus";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { confirmDialog } from "../components/ConfirmDialog";
import DetailCard from "../components/DetailCard";
import DataFormCard from "../components/DataFormCard";

export default function Department() {
  const axiosPrivate = useAxiosPrivate();
  const [depts, setDepts] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    async function fetchData() {
      try {
        const response = await axiosPrivate.get("/departments", {
          signal: controller.signal,
        });
        if (isMounted) {
          setDepts(response.data);
        }
      } catch (error) {
        if (isMounted && error.message !== 'canceled' && error.name !== "CanceledError" && error.name !== "AbortError") {
          console.error("Error fetching departments:", error);
        }
      }
    }
    fetchData();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []); // axiosPrivate is stable, doesn't need to be in deps

  const ODD_OPACITY = 0.2;

  const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
    [`& .${gridClasses.row}.even`]: {
      backgroundColor: theme.palette.grey[200],
      "&:hover, &.Mui-hovered": {
        backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
        "@media (hover: none)": {
          backgroundColor: "transparent",
        },
      },
      "&.Mui-selected": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY + theme.palette.action.selectedOpacity
        ),
        "&:hover, &.Mui-hovered": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY +
              theme.palette.action.selectedOpacity +
              theme.palette.action.hoverOpacity
          ),
          // Reset on touch devices, it doesn't add specificity
          "@media (hover: none)": {
            backgroundColor: alpha(
              theme.palette.primary.main,
              ODD_OPACITY + theme.palette.action.selectedOpacity
            ),
          },
        },
      },
    },
  }));

  const emptyTemplate = { DeptCom: '', DeptName: '', Short: '', InvDay: '', LoosGovt: '', Active: '', PGS: '', OnLine: '', Remarks: '' };

  const dept_columns: GridColDef[] = [
    { field: "DeptCom", headerName: "Dept Com", flex: 2 },
    {
      field: "DeptName",
      headerName: "Dept Name",
      flex: 6,
      editable: true,
    },
    {
      field: "Short",
      headerName: "Short",
      flex: 2,
      editable: true,
    },
    {
      field: "InvDay",
      headerName: "Inv Day",
      flex: 3,
      editable: true,
    },
    {
      field: "LoosGovt",
      headerName: "Loos Govt",
      flex: 1,
      editable: true,
    },
    {
      field: "Active",
      headerName: "Active",
      flex: 1,
      editable: true,
    },
    {
      field: "PGS",
      headerName: "PGS",
      flex: 1,
      editable: true,
    },
    {
      field: "OnLine",
      headerName: "On Line",
      flex: 1,
      editable: true,
    },
    {
      field: "Remarks",
      headerName: "Remarks",
      flex: 4,
      editable: true,
    },
    {
      field: "action",
      headerName: "Action",
      renderCell: (params) => {
        const onClick = (e) => {
          console.log("delete department", params);
          confirmDialog(
            `Do you really want to delete department: ${params.row.DeptName}?`,
            () => deleteDept(params.row.DeptCom)
          );
        };

        return (
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={onClick}
            >
              Delete
            </Button>
          </Stack>
        );
      },
    },
  ];

  const handleOnRowClick = async (rowData) => {
    console.log("Clicked ->" + JSON.stringify(rowData));
    setIsCreating(false);
    setSelectedRow(rowData);
  };


  async function refetchAllDepartments() {
    try {
      const response = await axiosPrivate.get("/departments");
      setDepts(response.data);
    } catch (error) {
      console.error("Error refetching departments:", error);
    }
  }

  const addDept = async (deptData) => {
    try {
      const response = await axiosPrivate.post("/departments/addDept", deptData);
      await refetchAllDepartments();
    } catch (error) {
      if (error.response && error.response.status === 400) {
        confirmDialog(error.response.data.message, () => {}, true);
      } else {
        console.error("Error adding department:", error);
      }
    }
  }

  const updateDept = async (deptData) => {
    try {
      // Assuming you have an endpoint for updating department, e.g., /departments/updateDept
      const response = await axiosPrivate.post("/departments/updateDept", deptData);
      await refetchAllDepartments();
    } catch (error) {
      if (error.response && error.response.status === 400) {
        confirmDialog(error.response.data.message, () => {}, true);
      } else {
        console.error("Error updating department:", error);
      }
    }
  } 

  const deleteDept = async (deptCom) => {
    try {
      const response = await axiosPrivate.post("/departments/deleteDept", {
        DeptCom: deptCom,
      });

      setSelectedRow(null);
      setIsCreating(false);

      await refetchAllDepartments();
    } catch (error) {
      console.error("Error deleting department:", error);
    }
  };

 
  return (
    <Stack
      spacing={2}
      ml={2}
      maxWidth="xs"
      sx={{ width: "100%" }}
      alignItems="left"
    >
      <Box className="d-flex flex-row justify-content-between" width="85%">
        <h2>
          Department
          <Button onClick={refetchAllDepartments}>
            <FiRefreshCcw />
          </Button>
        </h2>
      <Button variant="contained" onClick={() => setIsCreating(true)}>Add New</Button>      
      </Box>

      <Box sx={{ height: selectedRow || isCreating ? 300 : 700, width: "85%" }}>
        <StripedDataGrid
          onRowClick={(param) => handleOnRowClick(param.row)}
          rowHeight={25}
          rows={depts}
          getRowId={(row) => row.DeptCom}
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          columns={dept_columns}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
          }
        />
      {(selectedRow || isCreating) && (
        <DataFormCard 
          // If creating, use template. If editing, use selectedRow.
          initialData={isCreating ? emptyTemplate : selectedRow} 
          isCreate={isCreating}
          onSave={(data) => {
            console.log("Saving Data:", data);
            if (isCreating) {
              addDept(data);
            } else {
              updateDept(data);
            }
            setSelectedRow(null);
            setIsCreating(false);
          }}
          onCancel={() => {
            setSelectedRow(null);
            setIsCreating(false);
          }}
        />
      )}
      </Box>

     {/* The DetailCard appears only when a row is clicked */}
      {/* {selectedRow && (
        <DetailCard 
          data={selectedRow} 
          title={`Details for ${selectedRow.DeptName || 'Selected Row'}`} 
        />
      )} */}


    </Stack>
  );
}
